/**
 * Utilidades para formateo de números de teléfono.
 * El componente PhoneInput se encarga de la selección de país;
 * estas funciones solo limpian y formatean para display/links.
 */

/**
 * Normaliza un número de teléfono para almacenamiento.
 * Remueve espacios, guiones y paréntesis.
 * Espera que el número ya venga con código de país (+XXX...) desde PhoneInput.
 * 
 * @param phone - Número a normalizar
 * @returns Número limpio (ej: +59899123456)
 */
export function normalizePhone(phone: string): string {
  if (!phone) return phone;
  
  return phone.replace(/[\s\-\(\)]/g, '');
}

/**
 * Valida que un número de teléfono tenga formato correcto
 * Acepta números de Uruguay, Argentina, Brasil, Chile, Paraguay
 * 
 * @param phone - Número a validar
 * @returns true si el formato es válido
 */
function isValidPhone(phone: string): boolean {
  if (!phone) return false;
  
  const normalized = normalizePhone(phone);
  
  // Formato: + seguido de código de país y dígitos locales (mínimo 10 dígitos total, máximo 16)
  const phoneRegex = /^\+\d{10,16}$/;
  return phoneRegex.test(normalized);
}

/**
 * Formatea un número para mostrar en UI
 * +59899123456 -> +598 99 123 456
 * +5491112345678 -> +54 9 11 1234 5678
 * 
 * @param phone - Número a formatear
 * @returns Número formateado para display
 */
function formatPhoneForDisplay(phone: string): string {
  if (!phone) return phone;
  
  const normalized = normalizePhone(phone);
  
  // Formato uruguayo: +598 XX XXX XXX
  if (normalized.startsWith('+598')) {
    const digits = normalized.substring(4);
    if (digits.length >= 8 && digits.length <= 9) {
      return `+598 ${digits.substring(0, 2)} ${digits.substring(2, 5)} ${digits.substring(5)}`;
    }
  }
  
  // Formato argentino: +54 X XX XXXX XXXX
  if (normalized.startsWith('+54')) {
    const digits = normalized.substring(3);
    if (digits.length >= 10) {
      return `+54 ${digits.substring(0, 1)} ${digits.substring(1, 3)} ${digits.substring(3, 7)} ${digits.substring(7)}`;
    }
  }
  
  return normalized;
}
